import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

export const useStyles = makeStyles(({
  breakpoints,
  fontFamilies,
  fontSizes,
  lineHeights,
  fontWeights,
  siteMinWidth,
  borderRadiuses,
  palette,
}: Theme) => ({
  root: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minWidth: siteMinWidth,
    margin: '0 auto',
    filter: 'brightness(1)', // container for a child with position 'fixed'
    [breakpoints.up('sm')]: {
      flexDirection: 'column-reverse',
      marginBottom: 0,
      zIndex: 'unset',
    },
    [breakpoints.up('xl')]: {
      flexDirection: 'row',
    },
  },
  fullHeight: {
    minHeight: 'calc(100vh - 5rem)',
    '& > div:first-of-type': {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
    },
  },
  title: {
    margin: '0 0 16px',
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.lg,
    lineHeight: lineHeights.lg,
    fontWeight: fontWeights.medium,
    [breakpoints.up('sm')]: {
      marginBottom: 28,
      fontSize: fontSizes.xl,
      lineHeight: 1.3,
    },
  },
  ecpLayerRoot: {
    paddingTop: 24,
    [breakpoints.up('sm')]: {
      padding: '40px 0 0',
    },
    [breakpoints.up('xl')]: {
      width: '42%',
      maxWidth: 605,
      padding: '48px 0 0',
    },
  },
  ecpLayerRootCard: {
    position: 'relative',
    zIndex: 1,
    borderRadius: '16px 16px 0 0',
    background: palette.common.white,
    [breakpoints.up('xl')]: {
      marginTop: 0,
      borderRadius: borderRadiuses.xs,
      background: 'transparent',
    },
  },
  ecpLayerWrapper: {
    padding: '0 16px',
    [breakpoints.up('sm')]: {
      padding: '0 40px',
    },
    [breakpoints.up('xl')]: {
      padding: '0 48px',
    },
  },
  shadowWrapper: {
    boxShadow: '0px 4px 6px -1px rgba(26, 32, 44, 0.1), 0px 2px 4px -1px rgba(26, 32, 44, 0.06)',
    [breakpoints.up('sm')]: {
      boxShadow: 'none',
    },
  },
  mapLayer: {
    height: 'calc(100vh - 159px)',
    width: '100%',
    marginBottom: -60,
    [breakpoints.up('sm')]: {
      height: '55vh',
      display: 'flex',
      marginBottom: 0,
    },
    [breakpoints.up('xl')]: {
      position: 'sticky',
      top: '5rem',
      width: '58%',
      flexGrow: 1,
      height: 'calc(100vh - 80px)',
    },
  },
  mapLayerHidden: {
    display: 'none',
  },
  searchBar: {
    display: 'grid',
    gridColumnGap: '8px',
    gridTemplateColumns: 'calc(100% - 46px) 37px',
    alignItems: 'start',
    paddingBottom: 12,
    [breakpoints.up('sm')]: {
      gridTemplateColumns: 'calc(100% - 200px) 170px 40px',
      paddingBottom: 24,
    },
    '& > .MuiIconButton-root': {
      marginTop: 8,
      [breakpoints.up('sm')]: {
        marginTop: 0,
      },
    },
    '& form': {
      lineHeight: 1.43,
    },
  },
  internetShopsButtonWrapper: {
    [breakpoints.up('sm')]: {
      border: '1px solid red',
      borderRadius: '50%',
    },
  },
  filterButtonDesktop: {
    display: 'none',
    [breakpoints.up('sm')]: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      borderRadius: borderRadiuses.xs,
      boxShadow: 'none',
      backgroundColor: 'transparent',
    },
  },
  sortButtons: {
    width: '100%',
    [breakpoints.up('sm')]: {
      width: 330,
    },
  },
  storesList: {
    [breakpoints.up('sm')]: {
      margin: '-12px 0 0',
      paddingBottom: '1.75rem',
      bottom: 0,
    },
    [breakpoints.up('md')]: {
      minHeight: 'unset',
      paddingBottom: '2.25rem',
    },
  },
  mobileButtonsBar: {
    position: 'sticky',
    bottom: '4rem',
    left: 0,
    right: 0,
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 0,
    marginBottom: '4rem',
    padding: '0 1rem',
    [breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  listButtons: {
    bottom: '3rem',
    marginBottom: '3rem',
  },
  mobileButtonsCenter: {
    position: 'relative',
    left: '50%',
    transform: 'translateX(-50%)',
    '& .MuiButton-root': {
      minHeight: '3rem',
      padding: '0.625rem 1rem',
      borderRadius: '1.5rem',
      lineHeight: 1.2,
      fontWeight: fontWeights.medium,
    },
  },
  mobileViewButton: {
    position: 'relative',
    width: 'fit-content',
    right: 16,
    marginLeft: 'auto',
    zIndex: 10,
  },
  mobileViewButtonStyle: {
    background: palette.common.white,
    border: 'none',
    borderRadius: '1.5rem',
    padding: '0.75rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
  inputRoot: {
    minWidth: 'unset',
    '&.MuiInput-underline:after': {
      borderBottomColor: palette.primary.main,
    },
    '&.MuiOutlinedInput-root': {
      borderRadius: borderRadiuses.md,
      padding: '0 0 0 16px',
      '& fieldset': {
        borderColor: palette.grey['500'],
      },
      '&.Mui-focused fieldset': {
        borderColor: palette.primary.main,
      },
    },
  },
  input: {
    boxSizing: 'border-box',
    height: 43,
    color: palette.text.primary,
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.sm,
    padding: '12px 0',
    lineHeight: 1,
  },
  appointmentWrapper: {
    position: 'absolute',
    top: 90,
    left: 16,
    width: 'calc(100% - 32px)',
    [breakpoints.up('sm')]: {
      top: 16,
    },
    [breakpoints.up('xl')]: {
      left: 20,
      width: 'calc(100% - 40px)',
    },
    '&:empty': {
      display: 'none',
    },
  },
}));
